import { Fragment, useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faCircleInfo,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { useChatContext } from 'providers/ChatProvider';
import ConversationStarter from './ConversationStarter';
import ChatContentFooter from './ChatContentFooter';
import Message from '../message';
import { useAppContext } from 'providers/AppProvider';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { API_BASE } from 'apiConfig';
import { setCustomerHistory, endChat, sendAgentRead } from 'store/slices/trafficSlice';
import BlockCustomerButton from 'components/modules/chat/BlockCustomerButton';
import { formatMessageDate, localDayKey } from 'helpers/datetime';

const initialsOf = (name?: string) => {
  const n = (name || '').trim();
  if (!n) return 'V';
  const parts = n.split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'V';
};

const generateUniqueId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`;

// Show a full country name as-is, but a 2-letter code uppercased (pt -> PT).
const prettyCountry = (countryName?: string, code?: string) => {
  const n = (countryName || '').trim();
  if (n.length > 2) return n; // full name like "Portugal"
  const c = (n || code || '').trim();
  return c ? c.toUpperCase() : '';
};

// Friendly OS label — also normalizes legacy raw values like "Win32"/"MacIntel".
const prettyOs = (os?: string) => {
  const s = (os || '').toLowerCase();
  if (/iphone|ipod/.test(s)) return 'iPhone';
  if (/ipad/.test(s)) return 'iPad';
  if (/android/.test(s)) return 'Android';
  if (/win/.test(s)) return 'Windows';
  if (/mac|darwin/.test(s)) return 'Mac';
  if (/linux|x11|cros/.test(s)) return 'Linux';
  if (/mobi|phone/.test(s)) return 'Phone';
  return os || 'n/a';
};

const ChatContent = ({ currentChatCustomerID }: any) => {
  const customers = useSelector((state: RootState) => state.traffic.customers);
  const token = useSelector((state: RootState) => state.auth.token);
  const license_uuid = useSelector((state: RootState) => state.auth.license_uuid);
  const dispatch = useDispatch();

  const {
    config: { isDark }
  } = useAppContext();
  const { setShowUserListOffcanvas } = useChatContext();

  const [showDetails, setShowDetails] = useState(true);

  const getCurrentCustomer = (customer_id: any) =>
    customers.find(item => item.customer_id === customer_id);
  const myCustomer = getCurrentCustomer(currentChatCustomerID);

  const messageEndRef = useRef<null | HTMLSpanElement>(null);

  // Load the persisted conversation history when a chat is opened. The live
  // Redux store only holds messages received during this session.
  useEffect(() => {
    if (!currentChatCustomerID) return;
    let cancelled = false;
    fetch(`${API_BASE}/chats/${currentChatCustomerID}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    })
      .then(r => (r.ok ? r.json() : []))
      .then((rows: any[]) => {
        if (cancelled || !Array.isArray(rows)) return;
        const messages = rows
          .filter(r => r && ((r.message != null && r.message !== '') || r.attachment_url))
          .map(r => ({
            id: String(r.id),
            content: r.message || '',
            type: r.who_sent_message === 'agent' ? 'agent' : 'customer',
            timestamp: r.datemade,
            agentRead: true,
            read: !!r.read,
            agentName: r.agent_name || undefined,
            agentAvatar: r.avatar_url || undefined,
            attachment: r.attachment_url
              ? { url: r.attachment_url, type: r.attachment_type, meta: r.attachment_meta || {} }
              : null
          }));
        dispatch(setCustomerHistory({ customer_id: currentChatCustomerID, messages }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [currentChatCustomerID, token, dispatch]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView();
  }, [myCustomer?.messages]);

  // While actively viewing a conversation (this browser tab in the foreground),
  // tell the visitor we've read their messages — only then. Catches up when the
  // agent returns to the tab.
  useEffect(() => {
    if (!currentChatCustomerID) return;
    const markRead = () => {
      if (document.hidden) return;
      const hasCustomerMsg = (myCustomer?.messages || []).some(
        (m: any) => m.type === 'customer'
      );
      if (hasCustomerMsg) dispatch(sendAgentRead({ customerId: currentChatCustomerID }));
    };
    markRead();
    document.addEventListener('visibilitychange', markRead);
    window.addEventListener('focus', markRead);
    return () => {
      document.removeEventListener('visibilitychange', markRead);
      window.removeEventListener('focus', markRead);
    };
  }, [currentChatCustomerID, myCustomer?.messages?.length, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!currentChatCustomerID) return <></>;

  const name = (myCustomer?.name || '').trim() || 'Unnamed visitor';
  const initials = initialsOf(myCustomer?.name);
  const isActive = !!myCustomer?.active;
  const chatStarted = !!myCustomer?.chat_started;
  const location = [
    myCustomer?.region,
    myCustomer?.city,
    prettyCountry(myCustomer?.country_name, myCustomer?.country)
  ]
    .filter(Boolean)
    .join(', ');

  const avatarStyle = (size: number): React.CSSProperties => ({
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 500,
    fontSize: Math.round(size * 0.36),
    background: isDark ? '#33363d' : '#e7ebf0',
    color: isDark ? '#cdd2da' : '#3d4a5c',
    flexShrink: 0
  });

  const onlineDot: React.CSSProperties = {
    position: 'absolute',
    right: -1,
    bottom: -1,
    width: 11,
    height: 11,
    borderRadius: '50%',
    background: '#25b865',
    border: `2px solid ${isDark ? '#1f2123' : '#ffffff'}`
  };

  const endChatNow = () =>
    dispatch(
      endChat({
        messageId: generateUniqueId(),
        customerId: currentChatCustomerID,
        message: 'Support agent ended the chat.',
        ownerToken: token || '',
        license_uuid: license_uuid || ''
      })
    );

  const getCountryImageSRC = (country?: string) =>
    country ? `https://cdn.easychatdesk.com/flags/${country.toLowerCase()}.svg` : '';

  const visits: any[] = Array.isArray(myCustomer?.history) ? myCustomer!.history : [];

  return (
    <>
      <Card className="h-100" style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <Card.Header className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
            <Button
              className="px-1 text-body-tertiary d-sm-none"
              onClick={() => setShowUserListOffcanvas(true)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
            <div className="position-relative">
              <div style={avatarStyle(40)}>{initials}</div>
              {isActive && <span style={onlineDot} />}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="fw-semibold text-body-emphasis lh-sm text-truncate">
                {name}
              </div>
              <div className="fs-9 text-body-secondary text-truncate">
                <span className={isActive ? 'text-success' : ''}>
                  {isActive ? 'Active now' : 'Offline'}
                </span>
                {location && <> · {location}</>}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-1 flex-shrink-0">
            {isActive && chatStarted && (
              <Button variant="phoenix-danger" size="sm" onClick={endChatNow}>
                End chat
              </Button>
            )}
            <BlockCustomerButton
              customerId={currentChatCustomerID}
              blocked={myCustomer?.blocked}
            />
            <Button
              className="btn-icon text-body-tertiary"
              onClick={() => setShowDetails(v => !v)}
              aria-label="Toggle details"
            >
              <FontAwesomeIcon icon={faCircleInfo} />
            </Button>
          </div>
        </Card.Header>

        {/* Thread */}
        <Card.Body
          className="p-3 p-sm-4 d-flex flex-column"
          style={{ background: isDark ? '#16171b' : '#f6f7f9', overflowY: 'auto' }}
        >
          {(!myCustomer || myCustomer.messages.length === 0) && <ConversationStarter />}
          {(() => {
            let lastDay = '';
            return myCustomer?.messages.map((message: any, index: number) => {
              const dayKey = localDayKey(message.timestamp);
              const showSeparator = dayKey !== '' && dayKey !== lastDay;
              lastDay = dayKey;
              return (
                <Fragment key={index}>
                  {showSeparator && (
                    <div className="align-self-center my-2">
                      <span
                        className="badge rounded-pill"
                        style={{
                          background: isDark ? '#26282e' : '#ffffff',
                          color: isDark ? '#9aa0aa' : '#98a2b3',
                          border: `0.5px solid ${isDark ? '#33363d' : '#eaecf0'}`,
                          fontWeight: 400,
                          fontSize: 11
                        }}
                      >
                        {formatMessageDate(message.timestamp)}
                      </span>
                    </div>
                  )}
                  <Message message={message} peerInitials={initials} />
                </Fragment>
              );
            });
          })()}
          <span ref={messageEndRef} />
        </Card.Body>

        <ChatContentFooter currentChatCustomerID={currentChatCustomerID} />
      </Card>

      {/* Contact details */}
      {showDetails && (
        <Card style={{ width: 250, flexShrink: 0 }} className="d-none d-md-block">
          <Card.Body className="p-3 d-flex flex-column gap-3 scrollbar">
            <div className="d-flex justify-content-end">
              <Button
                className="btn-icon btn-icon-sm text-body-tertiary"
                onClick={() => setShowDetails(false)}
                aria-label="Close details"
              >
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </div>

            <div className="d-flex flex-column align-items-center text-center gap-2">
              <div className="position-relative">
                <div style={avatarStyle(64)}>{initials}</div>
                {isActive && <span style={onlineDot} />}
              </div>
              <div>
                <div className="fw-semibold text-body-emphasis">{name}</div>
                {myCustomer?.email && (
                  <div className="fs-9 text-body-secondary">{myCustomer.email}</div>
                )}
              </div>
              <div className="fs-9 d-flex align-items-center gap-2">
                <span className={isActive ? 'text-success' : 'text-body-tertiary'}>
                  {isActive ? '● Online' : '○ Offline'}
                </span>
                {myCustomer?.country && (
                  <img src={getCountryImageSRC(myCustomer.country)} width={18} alt="" />
                )}
              </div>
              {location && (
                <div className="fs-9 text-body-secondary">{location}</div>
              )}
            </div>

            <div className="border-top pt-3">
              <div className="fs-10 text-uppercase text-body-tertiary mb-2 fw-semibold">
                Details
              </div>
              <table className="w-100 fs-9">
                <tbody>
                  {[
                    ['Browser', myCustomer?.browser],
                    ['OS', prettyOs(myCustomer?.os)],
                    ['Device', myCustomer?.device],
                    ['IP', myCustomer?.ip],
                    ['Referrer', myCustomer?.refer],
                    ['Current page', myCustomer?.title]
                  ].map(([label, value]) => (
                    <tr key={label as string}>
                      <td className="text-body-secondary py-1 pe-2">{label}</td>
                      <td className="text-end py-1 text-truncate" style={{ maxWidth: 120 }}>
                        {value || 'n/a'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-top pt-3">
              <div className="fs-10 text-uppercase text-body-tertiary mb-2 fw-semibold">
                Visited pages
              </div>
              {visits.length === 0 ? (
                <div className="fs-9 text-body-tertiary">No visits recorded</div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {visits
                    .slice()
                    .reverse()
                    .slice(0, 10)
                    .map((v: any, i: number) => (
                      <a
                        key={i}
                        href={v.visited_page}
                        title={v.visited_page}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fs-9 text-decoration-none text-truncate"
                      >
                        {(v.title || v.visited_page || '').toString().substring(0, 40) ||
                          'Untitled'}
                      </a>
                    ))}
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default ChatContent;
